import React, { useState } from "react";

import { useHistory } from "react-router-dom";
import { Form, Formik } from "formik";
import {
  FormHelperText,
  Grid,
  Link,
  makeStyles,
  Typography,
} from "@material-ui/core";
import * as yup from "yup";

import Button from "../../components/Button";
import logo from "../../assets/images/logo.svg";
import Logo from "../../components/Logo";
import Paper from "../../components/Paper";
import Background from "../../components/Background";
import { UserDetails } from "../../types";
import { routes } from "../../api/paths";
import { LoginTextField } from "../../components/LoginTextField";
import { errorCodes } from "../../constants/errors";
import { executeFormLoginAuthentication, getUserDetails } from "../../api/user";
import { appVersion } from "../../constants/version";
import MetaDecorator from "../../components/MetaDecorator";

const loginSchema = yup.object({
  email: yup
    .string()
    .required("Upišite email.")
    .email("Unesite ispravan email."),
  password: yup.string().required("Upišite lozinku."),
});

type Props = {
  onLoginSuccess: (userDetails: UserDetails) => void;
};

const useStyles = makeStyles((theme) => ({
  paper: {
    height: "25rem",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    [theme.breakpoints.only("xs")]: {
      height: "22rem",
    },
  },
  bottom: {
    position: "fixed",
    bottom: "2px",
    right: "4px",
    opacity: 0.5,
  },
}));

export default function Login({ onLoginSuccess }: Props) {
  const classes = useStyles();
  const history = useHistory();
  const [credentialsError, setCredentialsError] = useState("");

  const onLoginClicked = async (email: string, password: string) => {
    try {
      await executeFormLoginAuthentication(email, password);
      const userDetails: UserDetails = (await getUserDetails()).data;
      onLoginSuccess(userDetails);
      if (userDetails.authority === "NURSE") {
        history.push(routes.NURSE_HOMEPAGE_URL);
      } else if (userDetails.authority === "RODA") {
        history.push(routes.RODA_HOMEPAGE_URL);
      }
    } catch (reason: any) {
      if (reason.response && reason.response.status === errorCodes.FORBIDDEN) {
        setCredentialsError("Neispravni korisnički podaci.");
      }
    }
  };

  return (
    <>
      <MetaDecorator />
      <Background>
        <Grid
          container
          direction="column"
          justifyContent="center"
          alignItems="center"
        >
          <Grid item xs={8} lg={3}>
            <Paper className={classes.paper}>
              <Logo src={logo} />
              <Formik
                initialValues={{ email: "", password: "" }}
                validationSchema={loginSchema}
                onSubmit={(data) => {
                  onLoginClicked(data.email, data.password);
                }}
              >
                {({ handleSubmit }) => (
                  <Form onSubmit={handleSubmit}>
                    <LoginTextField
                      id="email"
                      label="Email"
                      name="email"
                      autoComplete="email"
                    />
                    <LoginTextField
                      id="password"
                      label="Lozinka"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                    />
                    {credentialsError && (
                      <FormHelperText error={true}>
                        {" "}
                        {credentialsError}{" "}
                      </FormHelperText>
                    )}

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      color="primary"
                      size="large"
                    >
                      Prijavi se
                    </Button>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        marginTop: 4,
                        marginBottom: 8,
                        cursor: "pointer",
                      }}
                    >
                      <Typography variant="subtitle2" color="primary">
                        <Link
                          onClick={() =>
                            history.push(routes.PASSWORD_RESET_URL)
                          }
                        >
                          Zaboravljena lozinka?
                        </Link>
                      </Typography>
                    </div>
                  </Form>
                )}
              </Formik>
            </Paper>
          </Grid>
        </Grid>
        <Typography className={classes.bottom}>{appVersion}</Typography>
      </Background>
    </>
  );
}
